import React, { JSX, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import {
  sankeyLinkHorizontal,
  SankeyLink as SankeyLinkType,
  SankeyNodeMinimal,
} from 'd3-sankey';
import { SankeyDataLink, SankeyDataNode } from 'types/sankey';

import { colorRectFunc } from './parse';
import { RectNode } from './sankey-rect';

export type PathLink = SankeyLinkType<SankeyDataNode, SankeyDataLink>;

const makeDPath = sankeyLinkHorizontal<SankeyDataNode, SankeyDataLink>();

interface SankeyLinkProps {
  d: string;
  strokeWidth?: number;
  color?: string;
  title?: string;
  source?: string;
  target?: string;
  link: PathLink;
  renderedLinksCache: Set<string>;
  setRenderedLinksCache: React.Dispatch<React.SetStateAction<Set<string>>>;
}

export const SankeyLink = ({
  d,
  strokeWidth,
  source,
  target,
  link,
  renderedLinksCache,
  setRenderedLinksCache,
}: SankeyLinkProps): JSX.Element => {
  const pathRef = useRef<SVGPathElement>(null);
  const linkKey = `${source}--${target}`;

  const uuid = `${(link.source as SankeyDataNode & SankeyNodeMinimal<SankeyDataNode, SankeyDataLink>).index}-${(link.target as SankeyDataNode & SankeyNodeMinimal<SankeyDataNode, SankeyDataLink>).index}`;

  const sourceNodeColor = colorRectFunc(link.source) as string;
  const targetNodeColor = colorRectFunc(link.target) as string;
  const sourceX = (
    link.source as SankeyNodeMinimal<SankeyDataNode, SankeyDataLink>
  ).x1;
  const sourceY = (
    link.source as SankeyNodeMinimal<SankeyDataNode, SankeyDataLink>
  ).y1;
  const targetX = (
    link.target as SankeyNodeMinimal<SankeyDataNode, SankeyDataLink>
  ).x0;
  const targetY = (
    link.target as SankeyNodeMinimal<SankeyDataNode, SankeyDataLink>
  ).y0;

  useEffect(() => {
    if (pathRef.current && !renderedLinksCache.has(linkKey)) {
      setRenderedLinksCache((prevSet) => {
        const newSet = new Set(prevSet);
        newSet.add(linkKey);
        return newSet;
      });
      const length = pathRef.current.getTotalLength();
      // Reset animation when component mounts
      if (pathRef.current) {
        pathRef.current.style.strokeDasharray = `${length} ${length}`;
        pathRef.current.style.strokeDashoffset = `${length}`;
        pathRef.current.style.strokeOpacity = link.opacity.toString();

        // Trigger animation
        setTimeout(() => {
          if (pathRef.current) {
            pathRef.current.style.transition =
              'stroke-dashoffset 0.2s ease-in-out';
            pathRef.current.style.strokeDashoffset = '0';
          }
        }, 50);
      }
    } else {
      if (pathRef && pathRef.current) {
        // Already cached: Don't animate, just make sure path updates properly
        pathRef.current.style.transition = 'none';
        pathRef.current.style.strokeDasharray = 'none';
        pathRef.current.style.strokeDashoffset = '0';
        pathRef.current.style.strokeOpacity = link.opacity.toString();
      }
    }
  }, [d, linkKey, link.opacity]);

  useEffect(() => {
    const svg = document.getElementById(`sankey-link-container-${uuid}`);
    const defs =
      svg?.querySelector('defs') ||
      svg?.appendChild(
        document.createElementNS('http://www.w3.org/2000/svg', 'defs')
      );
    while (defs && defs.firstChild) {
      defs.removeChild(defs.firstChild);
    }
    const gradient = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'linearGradient'
    );
    gradient.setAttribute('id', `linkGradient-${uuid}`);
    gradient.setAttribute('x1', sourceX!.toString());
    gradient.setAttribute('y1', sourceY!.toString());
    gradient.setAttribute('x2', targetX!.toString());
    gradient.setAttribute('y2', targetY!.toString());
    gradient.setAttribute('gradientUnits', 'userSpaceOnUse');

    const stop1 = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'stop'
    );
    stop1.setAttribute('offset', '0%');
    stop1.setAttribute('stop-color', sourceNodeColor);

    const stop2 = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'stop'
    );
    stop2.setAttribute('offset', '100%');
    stop2.setAttribute('stop-color', targetNodeColor);

    gradient.appendChild(stop1);
    gradient.appendChild(stop2);
    defs?.appendChild(gradient);
  }, [
    uuid,
    sourceNodeColor,
    targetNodeColor,
    sourceX,
    sourceY,
    targetX,
    targetY,
  ]);

  return (
    <g style={{ mixBlendMode: 'screen' }} id={`sankey-link-container-${uuid}`}>
      {/* Define linear gradient inside <defs> */}
      <defs>{/* linear gradients come here, dynamically */}</defs>
      <path
        ref={pathRef}
        d={d}
        stroke={`url(#linkGradient-${uuid})`}
        strokeOpacity={link.opacity}
        strokeWidth={strokeWidth}
        id={`path-link-${uuid}`}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onMouseOver={(e) => {
          if (link.isDisabled) return;
          e.currentTarget.style.strokeOpacity = `${link.opacity + 0.2}`;
          const tooltip = d3.select('#tooltip');
          const tooltipWidth = 120; // Approx width of tooltip
          const xPos = e.pageX + 10;
          const yPos = e.pageY - 28;

          tooltip
            .style(
              'left',
              `${Math.min(xPos, window.innerWidth - tooltipWidth)}px`
            )
            .style('top', `${Math.max(10, yPos)}px`)
            .style('opacity', 1)
            .html(
              `${source}(${(link.source as RectNode).value?.toLocaleString()}) → ${target}(${link.value.toLocaleString()})`
            );
        }}
        onMouseOut={(e) => {
          if (link.isDisabled) return;
          e.currentTarget.style.strokeOpacity = `${link.opacity}`;
          d3.select('#tooltip').style('opacity', 0);
        }}
      ></path>
    </g>
  );
};

interface SankeyLinksProps {
  links: PathLink[];
  titleFunc?(link: PathLink): string;
  colorFunc?(link: PathLink): string;
  renderedLinksCache: Set<string>;
  setRenderedLinksCache: React.Dispatch<React.SetStateAction<Set<string>>>;
}

export const SankeyLinks = ({
  links,
  titleFunc,
  colorFunc,
  renderedLinksCache,
  setRenderedLinksCache,
}: SankeyLinksProps): JSX.Element => {
  return (
    <g fill="none">
      {links.map((link) => {
        const d = makeDPath(link);

        if (!d) return null;

        const strokeWidth = Math.max(1, link.width || 0);
        const { source, target } = link;
        const sourceKey =
          typeof source === 'object'
            ? (source as RectNode).key
            : (source as string);
        const targetKey =
          typeof target === 'object'
            ? (target as RectNode).key
            : (target as string);
        const key = `${sourceKey}--${targetKey}`;
        const sourceName =
          typeof source === 'object'
            ? (source as RectNode).name
            : (source as string);
        const targetName =
          typeof target === 'object'
            ? (target as RectNode).name
            : (target as string);

        return (
          <SankeyLink
            key={key}
            d={d}
            color={colorFunc?.(link)}
            title={titleFunc?.(link)}
            strokeWidth={strokeWidth}
            source={sourceName}
            target={targetName}
            link={link}
            renderedLinksCache={renderedLinksCache}
            setRenderedLinksCache={setRenderedLinksCache}
          />
        );
      })}
    </g>
  );
};
